import React, { useRef, useState, useLayoutEffect } from 'react';
import { ButtonVariant, SecondaryButton } from '@uni-of-exeter/digital.lib.react.ui.kit';

import './digital-skills-topic-filter.css';

const FILTER_VISIBLE_ROWS = 1;

export type FilterTopicType = { id: string; title: string };
interface DigitalSkillsTopicFilterProps {
  values: FilterTopicType[];
  selectedValues?: FilterTopicType[];
  onChange: (value: FilterTopicType) => void;
  label: string;
  showMoreText: string;
  showLessText: string;
}

const DigitalSkillsTopicFilter: React.FC<DigitalSkillsTopicFilterProps> = ({
  values,
  selectedValues,
  onChange,
  label,
  showMoreText,
  showLessText,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [showToggle, setShowToggle] = useState(false);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const items = Array.from(el.children) as HTMLElement[];
      if (!items.length) return;

      const rows: number[] = [];
      let currentTop: number | null = null;

      for (const item of items) {
        const top = item.offsetTop;
        if (currentTop === null || top > currentTop) {
          rows.push(top);
          currentTop = top;
        }
      }

      if (rows.length > FILTER_VISIBLE_ROWS) {
        const rowHeight = items[0].offsetHeight;
        const gap = rows[1] - rows[0] - rowHeight;
        const height = rowHeight * FILTER_VISIBLE_ROWS + gap * (FILTER_VISIBLE_ROWS - 1);

        setMaxHeight(height);
        setShowToggle(true);
      } else {
        setMaxHeight(null);
        setShowToggle(false);
      }
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => ro.disconnect();
  }, [values]);

  return (
    <div className="topicFilterContainer">
      {values.length > 0 && (
        <>
          <div className="filterItem title">{label}</div>
          <div
            ref={containerRef}
            className={`digitalSkillsTopicFilter ${expanded ? 'expanded' : ''}`}
            style={!expanded && maxHeight ? { maxHeight } : undefined}
          >
            {values.map((topic) => (
              <SecondaryButton
                tabIndex={0}
                className={`filterItem ${selectedValues?.some((sv) => sv.id === topic.id) ? 'selected' : ''}`}
                key={topic.id}
                variant={ButtonVariant.Outline}
                onClick={() => onChange(topic)}
              >
                {topic.title}
              </SecondaryButton>
            ))}
          </div>
        </>
      )}
      {showToggle && (
        <SecondaryButton
          tabIndex={0}
          className="lessMoreButton"
          variant={ButtonVariant.Outline}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? showLessText : showMoreText}
        </SecondaryButton>
      )}
    </div>
  );
};

export default DigitalSkillsTopicFilter;
